import React, { useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import classNames from "classnames/bind";
import { IContentModel } from "models/dto/ZoomiLxp/Models/ContentModels/IContentModel";
import PlayerHtml from "../player-html/player-html";
import { ContentTypes } from "models/dto/ZoomiLxp/Utilities/Enumerations/ContentTypes";
import { observer } from "mobx-react";
import { useAppStore } from "store";
import { EventTypes } from "models/dto/ZoomiLxp/Utilities/Enumerations/EventTypes";
import { ISaveEventCourseItemHistoryModel } from "models/dto/ZoomiLxp/Models/EventModels/ISaveEventCourseItemHistoryModel";
import PlayerControlMini from "../player-control-mini/player-control-mini";
import { useScormDifficult } from "hooks/useScormDifficult";
import { IPlayerPropsBase } from "../data/data";
import { useContentFiles } from "hooks/useContentFiles";

interface IPlayerHtmlBoxProps extends IPlayerPropsBase {
	content: IContentModel;
}

const cx = classNames.bind(styles);

const PACKAGE_CONTENT_PATH = "/api/v1/LMS/PackageContent/";

const PlayerHtmlBox = (props: IPlayerHtmlBoxProps) => {
	const { contentStore, lmsStore } = useAppStore();
	const { content, className, onEventHandle } = props;
	const [packageUrl, setPackageUrl] = useState<string>();
	const wasEndEventSentRef = useRef(false);
	const playingRef = useRef(false);
	const currentPositionRef = useRef(0);
	const sessionIdRef = useRef<number>(contentStore.sessionId ?? 0);

	const isPackage =
		content.contentType === ContentTypes.Zip || !!content.fileContent?.url?.startsWith(PACKAGE_CONTENT_PATH);
	const sessionId = contentStore.sessionId;
	const { files } = useContentFiles(content, !isPackage);

	const sendStart = () => {
		const data: ISaveEventCourseItemHistoryModel = {
			sessionId: contentStore?.sessionId ?? 0,
			eventType: EventTypes.Start,
			payload: "",
		};
		Promise.allSettled([
			contentStore.sendEventCourseItemHistory(data),
			contentStore.sendEventCourseItemHistory({ ...data, eventType: EventTypes.Complete }),
		]);
	};

	const sendEndEventOnce = async () => {
		if (wasEndEventSentRef.current) return;
		wasEndEventSentRef.current = true;

		const currentPosition = currentPositionRef.current;
		const isPlaying = playingRef.current;
		const sessionId = sessionIdRef.current;

		const data: ISaveEventCourseItemHistoryModel = {
			sessionId,
			eventType: EventTypes.End,
			payload: JSON.stringify({
				currentPosition,
				isPlaying,
			}),
		};

		contentStore.stopKeepAlive();

		await onEventHandle(data);
	};

	const { params, onLoad } = useScormDifficult(sendStart);

	useEffect(() => {
		playingRef.current = params.playing;
	}, [params.playing]);

	useEffect(() => {
		currentPositionRef.current = params.currentProgress ?? 0;
	}, [params.currentProgress]);

	useEffect(() => {
		if (contentStore.sessionId) {
			sessionIdRef.current = contentStore.sessionId;
		}
	}, [contentStore.sessionId]);

	useEffect(() => {
		if (!isPackage || !content.id || !sessionId) return;
		let isActual = true;
		setPackageUrl(undefined);
		lmsStore
			.getPackageContentUrl(content.id, sessionId)
			.then((url) => isActual && setPackageUrl(url))
			.catch(() => isActual && setPackageUrl(""));
		return () => {
			isActual = false;
		};
	}, [isPackage, content.id, sessionId, lmsStore]);

	useEffect(
		() => {
			return () => {
				void sendEndEventOnce();
			};
		}, //eslint-disable-next-line
		[]
	);

	const src = isPackage ? packageUrl : files?.url;
	if (src === undefined && (isPackage || !files)) return <div className={cx(styles.player_html_box, className)} />;
	if (!src) return <div className={styles.player_html_box__nodata}>No data</div>;

	return (
		<div className={cx(styles.player_html_box, className)}>
			<PlayerHtml src={src} params={params} onLoad={onLoad} onEventHandle={onEventHandle}>
				<PlayerControlMini params={params} />
			</PlayerHtml>
		</div>
	);
};

export default observer(PlayerHtmlBox);
